// Service-to-WhatsApp handoff registry — verified MTFA contact numbers.
//
// EVERY number here was scraped live from https://www.mtfa.org/contact/ (and
// mirrored on https://www.mtfa.org/ihsan-casket/) on 2026-09-21 SGT. Any change
// needs a fresh read from the same page — do NOT invent numbers or reuse a
// generic hotline in place of a service-specific one, since dispatchers watch
// different WhatsApp inboxes.
//
// A registry, not free-form: every service key resolves to exactly one number.
// If a service isn't in this table we fall back to MTFA HQ so no user is ever
// silently dropped.

// Verified live 2026-09-21 from mtfa.org/contact.
// Each entry: label (human), phoneE164 (bare digits for wa.me, no + or spaces),
// phoneDisplay (formatted for tel: + display), inboxWatchedBy (short label for
// the button caption), email (fallback if the person prefers).
export const MTFA_CONTACTS = {
  // General / anything Ihsan Aid, bursaries, orphanage, HQ enquiries.
  hq: {
    label: 'MTFA HQ',
    phoneE164: '6567477556',
    phoneDisplay: '+65 6747 7556',
    email: 'adminmtfa@mtfa.org',
    inboxWatchedBy: 'MTFA HQ team',
  },
  ihsan_casket: {
    label: 'Ihsan Casket (24 hours)',
    phoneE164: '6531384022',
    phoneDisplay: '+65 3138 4022',
    email: 'info@ihsancasket.com',
    inboxWatchedBy: 'Ihsan Casket officer',
    hours: '24 / 7',
  },
  ikc_norris: {
    label: 'Ihsan Kidney Care — Norris Centre',
    phoneE164: '6568050429',
    phoneDisplay: '+65 6805 0429',
    email: 'ikcnurses@mtfa.org',
    inboxWatchedBy: 'IKC nurses',
  },
  ikc_henderson: {
    label: 'Ihsan Kidney Care — Henderson Centre',
    phoneE164: '6560220284',
    phoneDisplay: '+65 6022 0284',
    email: 'ikchenderson@mtfa.org',
    inboxWatchedBy: 'IKC Henderson nurses',
  },
  ikc_yishun: {
    label: 'Ihsan Kidney Care — Yishun Centre',
    phoneE164: '6560380491',
    phoneDisplay: '+65 6038 0491',
    email: 'ikcyishun@mtfa.org',
    inboxWatchedBy: 'IKC Yishun nurses',
  },
};

// Map a service item id (as it appears in the catalogue) to a contact key.
// Anything not listed here falls back to `hq` so no user is left stranded.
// This is the ONE place that couples product IDs to WhatsApp inboxes; add a
// row here whenever a new service ships.
const SERVICE_ID_TO_CONTACT_KEY = {
  // service hub items
  'financial-assistance': 'hq',
  'ihsan-aid': 'hq',
  bursaries: 'hq',
  'ihsan-casket': 'ihsan_casket',
  // dialysis centres — ids from mockData.dialysisCentres
  'ihsan-kidney-care': 'ikc_norris', // umbrella card (falls back to Norris)
  'ihsan-kidney-care-norris': 'ikc_norris',
  'ihsan-kidney-care-henderson': 'ikc_henderson',
  'ihsan-kidney-care-yishun': 'ikc_yishun',
  // Learn/volunteer/give handoffs currently route to HQ.
  // Additional service-specific inboxes get added here as they come online.
};

export function contactKeyFor(item) {
  if (!item) return 'hq';
  if (typeof item === 'string') return SERVICE_ID_TO_CONTACT_KEY[item] || 'hq';
  const explicit = item.contactKey || item.contact_key;
  if (explicit && MTFA_CONTACTS[explicit]) return explicit;
  if (item.id && SERVICE_ID_TO_CONTACT_KEY[item.id]) return SERVICE_ID_TO_CONTACT_KEY[item.id];
  return 'hq';
}

export function contactFor(item) {
  const key = contactKeyFor(item);
  return { key, ...MTFA_CONTACTS[key] };
}

// wa.me link builder. Returns a string URL that opens in WhatsApp on mobile
// (deep-link) and web.whatsapp.com on desktop (WhatsApp handles the switch);
// same URL either way, per WhatsApp's public spec. Text is prefilled but
// EDITABLE — recipient sees a draft, nothing is sent automatically.
//
//   waLinkForContact('ihsan_casket', { text: 'Salam, I need to arrange...' })
//   waLinkForContact('hq', { serviceName: 'Ihsan Aid', ref: 'IA-2026-0812' })
export function waLinkForContact(keyOrItem, options = {}) {
  const key = typeof keyOrItem === 'string' && MTFA_CONTACTS[keyOrItem]
    ? keyOrItem
    : contactKeyFor(keyOrItem);
  const contact = MTFA_CONTACTS[key] || MTFA_CONTACTS.hq;
  const text = options.text
    || buildDefaultMessage({
      serviceName: options.serviceName,
      ref: options.ref,
      memberName: options.memberName,
    });
  const params = text ? `?text=${encodeURIComponent(text)}` : '';
  return `https://wa.me/${contact.phoneE164}${params}`;
}

export function telLinkForContact(keyOrItem) {
  const key = typeof keyOrItem === 'string' && MTFA_CONTACTS[keyOrItem]
    ? keyOrItem
    : contactKeyFor(keyOrItem);
  const contact = MTFA_CONTACTS[key] || MTFA_CONTACTS.hq;
  return `tel:+${contact.phoneE164}`;
}

function buildDefaultMessage({ serviceName, ref, memberName }) {
  const bits = ['Salam MTFA,'];
  if (serviceName) {
    bits.push(`I would like to enquire about ${serviceName}.`);
  } else {
    bits.push('I would like to enquire about an MTFA service.');
  }
  if (memberName) bits.push(`My name is ${memberName}.`);
  if (ref) bits.push(`My reference is ${ref}.`);
  return bits.join(' ');
}

// Open a wa.me link in a new tab. Works uniformly on mobile (WhatsApp app
// intercepts wa.me) and desktop (WhatsApp Web). We always pass
// noopener,noreferrer so the opened page cannot navigate ours.
export function openWhatsAppForContact(keyOrItem, options = {}) {
  const url = waLinkForContact(keyOrItem, options);
  if (typeof window !== 'undefined' && typeof window.open === 'function') {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  return url;
}
